/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { CreateScale } from './components/CreateScale';
import { Controller } from './components/Controller';
import { Geometric } from './components/Geometric';
import { Sine } from './components/Sine';
import { Evenly } from './components/Evenly';

interface createScaleInterface {
  geometric: number[][],
  sine: number[][],
  evenly: number[][]
}

export const Generator: React.VFC = () => {
  const
    [getScaleData, setScaleData] = useState<createScaleInterface>({
      geometric: [],
      sine: [],
      evenly: [],
    }),
    [getSourceRGB, setSourceRGB] = useState({
      r: [0, 255],
      g: [0, 255],
      b: [0, 255]
    }),
    [getScaleStep, setScaleStep] = useState(15),
    [getPickerTextColor, setPickerTextColor] = useState({
      start: '#fff',
      end: '#000'
    }),
    stepValues = [9, 11, 13, 15, 17, 19, 21, 23, 25];

  const fetchColor = useCallback((event): void => {
    if(event.target.id === 'start') {
      setSourceRGB({
        r: [convertHEXtoRGB(event.target.value).r, getSourceRGB.r[1]],
        g: [convertHEXtoRGB(event.target.value).g, getSourceRGB.g[1]],
        b: [convertHEXtoRGB(event.target.value).b, getSourceRGB.b[1]]
      });
    } else if(event.target.id === 'end') {
      setSourceRGB({
        r: [getSourceRGB.r[0], convertHEXtoRGB(event.target.value).r],
        g: [getSourceRGB.g[0], convertHEXtoRGB(event.target.value).g],
        b: [getSourceRGB.b[0], convertHEXtoRGB(event.target.value).b]
      });
    }
  }, [getSourceRGB]);

  const fetchStep = useCallback((event): void => {
    setScaleStep(Number(event.target.value))
  }, [getScaleStep])

  const convertHEXtoRGB = useMemo(() => (source: string) => {
    const 
      hex = {
        r: source.slice(1,3),
        g: source.slice(3,5),
        b: source.slice(5,7)
      },
      rgb = {
        r: parseInt(hex.r, 16),
        g: parseInt(hex.g, 16),
        b: parseInt(hex.b, 16),
      };
    return rgb;
  }, [getSourceRGB])

  const updatePickerTextColor = useCallback((event): void => {
    const pickerColorNumber = Object.values(convertHEXtoRGB(event.target.value)).reduce((prev, current) => {
      return prev + current
    });
    if(pickerColorNumber < (256 * 3) / 2) {
      if(event.target.id === 'start') {
        setPickerTextColor({
          start: '#fff',
          end: getPickerTextColor.end
        });
      } else if(event.target.id === 'end') {
        setPickerTextColor({
          start: getPickerTextColor.start,
          end: '#fff'
        });
      }
    } else {
      if(event.target.id === 'start') {
        setPickerTextColor({
          start: '#000',
          end: getPickerTextColor.end
        });
      } else if(event.target.id === 'end') {
        setPickerTextColor({
          start: getPickerTextColor.start,
          end: '#000'
        });
      }
    }
  }, [getPickerTextColor]);

  const convertDECtoHEX = useCallback((decimal: number): string => {
    return decimal.toString(16).length === 1 ? `0${decimal.toString(16)}` : decimal.toString(16);
  }, [])

  const generate = (): void => {
    setScaleData(CreateScale(getSourceRGB, getScaleStep));
  }

  const init = (): void  => {
    setScaleData(CreateScale(getSourceRGB, getScaleStep));
  }

  useEffect(() => {
    init();
  }, []);

  return (
    <>
      <Controller 
        values={
          {
            getSourceRGB,
            getPickerTextColor,
            getScaleStep,
            stepValues,
          }
        }
        methods={
          {
            fetchColor,
            fetchStep,
            updatePickerTextColor,
            convertDECtoHEX,
            generate
          }
        } 
      />
      <div className='p-app-scale-section'>
        <Geometric 
          values={getScaleData.geometric}
          methods={{ convertDECtoHEX }}
        />
        <Sine 
          values={getScaleData.sine}
          methods={{ convertDECtoHEX }}
        />
        <Evenly 
          values={getScaleData.evenly}
          methods={{ convertDECtoHEX }}
        />
      </div>
    </>
  );
}